<template>
	<header class="SectionHeading" :class="classes">
		<div class="SectionHeading-heading">
			<!-- @slot CloHeading component should go here -->
			<slot name="heading" />
		</div>
		<div class="SectionHeading-filter">
			<!-- @slot Any text such as a subheading or filter value can go here -->
			<slot name="filter" />
		</div>
		<div class="SectionHeading-link" :class="linkClasses">
			<!-- @slot A link or button can go here -->
			<slot name="action" />
		</div>
	</header>
</template>

<script>
export default {
	components: {},
	props: {
		/**
		 * A horizontal line, can be 'above' or 'below'
		 */
		rule: {
			type: String,
			default: '',
		},
		/**
		 * Reverse styles for use on dark background
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * Aligns the link to the right
		 */
		right: {
			type: Boolean,
			default: false,
		},
		/**
		 * Styles the heading as a minor heading
		 */
		quiet: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes: function () {
			let classes = [];
			const prefix = 'SectionHeading--';
			if (this.reverse) {
				classes.push(prefix + 'reverse');
			}
			if (this.rule === 'above') {
				classes.push(prefix + 'ruleAbove');
			}
			if (this.rule === 'below') {
				classes.push(prefix + 'ruleBelow');
			}
			if (this.quiet) {
				classes.push(prefix + 'quiet');
			}
			return classes.join(' ');
		},
		linkClasses: function () {
			let classes = [];
			const prefix = 'SectionHeading-link--';
			if (this.right) {
				classes.push(prefix + 'right');
			}
			return classes.join(' ');
		},
	},
	data() {
		return {};
	},
};
</script>

<style lang="scss">
/* --------------------------------
	SectionHeading
		Title, filter (optional), link (optional)
		with options for link placement and rule
-------------------------------- */

.SectionHeading {
	@include stack-md;
	align-items: baseline;
	display: flex;
	flex-wrap: wrap;
	width: 100%;
	/* IE10+ styles hack */
	@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
		align-items: center;
	}
}

.SectionHeading-heading {
	@include inline-sm;
	.Heading {
		margin: 0; // remove default spacing under headings
	}
}

.SectionHeading-filter {
	@include inline-sm;
	@include text(2);
	color: $color-text-muted;
	text-transform: uppercase;
}

.SectionHeading-link {
	@include inline-sm;
	a:not(.Button) {
		@include text(3);
	}
	.Button {
		margin: 0; // remove button margin
	}
	&--right {
		margin-right: 0; // remove inline spacing when right aligned
		margin-left: auto;
		[dir='rtl'] & {
			margin-left: 0;
			margin-right: auto;
		}
	}
	&--middle {
		align-self: center;
	}
}

/* ----------------
	Variants
---------------- */

/* Reverse variant - for dark backgrounds */
.SectionHeading--reverse {
	@include colorReverse;
	border-bottom-color: $color-neutral-4;
	.SectionHeading-heading {
		color: $color-white;
	}
	.SectionHeading-filter {
		color: $color-text-muted-reverse;
	}
	a,
	.Button {
		color: $color-link-reverse;
		&:hover,
		&:focus,
		&:active {
			color: $color-link-hover-reverse;
		}
	}
	&.SectionHeading--rule {
		border-bottom: 1px solid $color-neutral-4;
	}
	&.SectionHeading--quiet {
		.SectionHeading-heading {
			color: $color-text-muted-reverse;
			.Heading {
				color: $color-text-muted-reverse;
			}
		}
	}
}

/* Rule variant - adds a light rule line underneath the component */
.SectionHeading--rule,
.SectionHeading--ruleBelow {
	padding-bottom: $space-xs;
	border-bottom: 1px solid $color-neutral-1;
}

/* Rule above variant - adds a light rule line over the component */
.SectionHeading--ruleAbove {
	padding-top: $space-xs;
	border-top: 1px solid $color-neutral-1;
}

/* Minor variant - less visual impact of heading */
.SectionHeading--quiet {
	.SectionHeading-heading {
		text-transform: uppercase;
		color: $color-text-muted;
	}
}
</style>
